// GrindZoneMapTab - v1.4 - Multi-Segment Zone Support
/* @ts-nocheck */
/* global google */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { PathLayer, PolygonLayer, TextLayer } from '@deck.gl/layers';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { RotateCwSquare, RotateCcwSquare, ArrowUpFromLine, ArrowDownFromLine } from 'lucide-react';

// ============================================================================
// CONFIGURATION & UTILITIES
// ============================================================================

const LIBRARIES = ['geometry'];

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const mapOptions = {
  disableDefaultUI: true,
  clickableIcons: false,
  mapTypeId: 'satellite',
  mapId: '24ee09cb1783e3c39c8fcb02',
  minZoom: 10,
  maxZoom: 23,
  gestureHandling: 'greedy',
  tilt: 45,
};

const formatStation = (station) => {
  if (station === null || station === undefined) return 'N/A';
  const hundreds = Math.floor(station / 100);
  const remainder = station % 100;
  return `${hundreds}+${remainder.toFixed(2).padStart(5, '0')}`;
};

const parseGeometry = (geom) => {
  try {
    if (typeof geom === 'object' && geom?.type === 'LineString' && geom.coordinates) {
      return geom;
    }
    if (typeof geom === 'string') {
      if (geom.startsWith('LINESTRING')) {
        const coordsStr = geom.replace('LINESTRING(', '').replace(')', '');
        const coords = coordsStr.split(',').map(pair => {
          const [lng, lat] = pair.trim().split(' ').map(parseFloat);
          return [lng, lat];
        }).filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));
        return { type: 'LineString', coordinates: coords };
      } 
      else if (geom.startsWith('{') || geom.startsWith('[')) {
        const parsed = JSON.parse(geom);
        if (parsed?.type === 'LineString' && parsed.coordinates) {
          return { type: 'LineString', coordinates: parsed.coordinates };
        }
      }
    }
    return null;
  } catch (err) {
    console.error('Error parsing geometry:', err);
    return null;
  }
};

const getDepthColor = (depth) => {
  if (depth < 0.25) return [255, 150, 150, 160];
  if (depth < 0.50) return [255, 80, 80, 180];
  return [220, 40, 40, 200];
};

const getALRColor = (magnitude) => {
  if (magnitude > 250) return [244, 67, 54, 200];
  if (magnitude > 200) return [255, 152, 0, 200];
  return [255, 193, 7, 200];
};

// ============================================================================
// GEOMETRY PROJECTION ENGINE
// ============================================================================

const getPointAtRatio = (coords, ratio) => {
  if (!coords || coords.length < 2) return coords ? coords[0] : [0,0];
  if (ratio <= 0) return coords[0];
  if (ratio >= 1) return coords[coords.length - 1];

  let totalLen = 0;
  const dists = [0];
  for (let i = 1; i < coords.length; i++) {
    const d = Math.sqrt(Math.pow(coords[i][0] - coords[i-1][0], 2) + Math.pow(coords[i][1] - coords[i-1][1], 2));
    totalLen += d;
    dists.push(totalLen);
  }

  const targetDist = totalLen * ratio;
  
  let i = 0;
  while (i < dists.length - 1 && dists[i+1] < targetDist) {
    i++;
  }
  
  const segmentLen = dists[i+1] - dists[i];
  if (segmentLen === 0) return coords[i];
  
  const segmentRatio = (targetDist - dists[i]) / segmentLen;
  const p1 = coords[i];
  const p2 = coords[i+1];
  
  return [
    p1[0] + (p2[0] - p1[0]) * segmentRatio,
    p1[1] + (p2[1] - p1[1]) * segmentRatio
  ];
};

const sliceLineString = (coords, startRatio, endRatio) => {
  if (!coords || coords.length < 2) return [];
  
  const r1 = Math.min(startRatio, endRatio);
  const r2 = Math.max(startRatio, endRatio);
  
  const pStart = getPointAtRatio(coords, r1);
  const pEnd = getPointAtRatio(coords, r2);
  
  const result = [pStart];
  
  let totalLen = 0;
  const dists = [0];
  for (let i = 1; i < coords.length; i++) {
    const d = Math.sqrt(Math.pow(coords[i][0] - coords[i-1][0], 2) + Math.pow(coords[i][1] - coords[i-1][1], 2));
    totalLen += d;
    dists.push(totalLen);
  }
  
  const startDist = totalLen * r1;
  const endDist = totalLen * r2;
  
  for (let i = 0; i < dists.length; i++) {
    if (dists[i] > startDist + 0.0000001 && dists[i] < endDist - 0.0000001) {
      result.push(coords[i]);
    }
  }
  
  result.push(pEnd);
  return result;
};

// ============================================================================
// MULTI-SEGMENT GEOMETRY BUILDER
// ============================================================================

/**
 * Build a continuous centerline for a zone that may span multiple segments.
 * Handles zones that cross segment boundaries.
 */
const buildZoneCenterline = (zone, segments) => {
  const zStart = zone.systemStartStation;
  const zEnd = zone.systemEndStation;
  
  // Sort segments by station
  const sortedSegments = [...segments].sort((a, b) => 
    Math.min(a.from_station_ft, a.to_station_ft) - Math.min(b.from_station_ft, b.to_station_ft)
  );
  
  // Find all segments that overlap with this zone
  const overlappingSegments = sortedSegments.filter(seg => {
    const segMin = Math.min(seg.from_station_ft, seg.to_station_ft);
    const segMax = Math.max(seg.from_station_ft, seg.to_station_ft);
    return segMax > zStart && segMin < zEnd;
  });
  
  if (overlappingSegments.length === 0) return null;
  
  // Build combined centerline from all overlapping segments
  const centerLinePoints = [];
  
  for (const segment of overlappingSegments) {
    const geom = parseGeometry(segment.geom);
    if (!geom?.coordinates || geom.coordinates.length < 2) continue;
    
    const segStart = Math.min(segment.from_station_ft, segment.to_station_ft);
    const segEnd = Math.max(segment.from_station_ft, segment.to_station_ft);
    const segLen = segEnd - segStart;
    
    if (segLen <= 0) continue;
    
    // Calculate what portion of this segment to include
    const clipStart = Math.max(zStart, segStart);
    const clipEnd = Math.min(zEnd, segEnd);
    
    if (clipEnd <= clipStart) continue;
    
    const startRatio = (clipStart - segStart) / segLen;
    const endRatio = (clipEnd - segStart) / segLen;
    
    const segmentPortion = sliceLineString(geom.coordinates, startRatio, endRatio);
    
    // Add points, avoiding duplicates at segment boundaries
    for (const point of segmentPortion) {
      if (centerLinePoints.length === 0) {
        centerLinePoints.push(point);
      } else {
        const lastPoint = centerLinePoints[centerLinePoints.length - 1];
        const dist = Math.sqrt(
          Math.pow(point[0] - lastPoint[0], 2) + 
          Math.pow(point[1] - lastPoint[1], 2)
        );
        // Only add if not a duplicate (more than ~1m apart)
        if (dist > 0.00001) {
          centerLinePoints.push(point);
        }
      }
    }
  }
  
  if (centerLinePoints.length < 2) return null;
  
  return centerLinePoints;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function GrindZoneMapTab({ 
  grindZones = [], 
  segments = [], 
  events = [], 
  selectedZoneId = null,
  onZoneSelect,
}) {
  const [map, setMap] = useState(null);
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [layerVisibility, setLayerVisibility] = useState({
    zones: true,
    alrs: true,
    segments: false,
  });
  const [hoverTooltip, setHoverTooltip] = useState(null);
  const deckOverlayRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
    version: 'beta',
  });

  const alrEvents = useMemo(() => {
    return (events || []).filter(e => 
      (e.event_type || '').toUpperCase() === 'LR' && e.geom
    );
  }, [events]);

  // --------------------------------------------------------------------------
  // CALCULATE ZONE POLYGONS (Multi-Segment Support)
  // --------------------------------------------------------------------------
  const zonePolygons = useMemo(() => {
    if (!grindZones.length || !segments.length) return [];

    const LANE_WIDTH_METERS = 3.66;
    const HALF_WIDTH = LANE_WIDTH_METERS / 2;

    return grindZones.map(zone => {
      // Use new multi-segment centerline builder
      const centerLine = buildZoneCenterline(zone, segments);
      
      if (!centerLine || centerLine.length < 2) {
        console.warn(`Could not build centerline for zone ${zone.id}`);
        return null;
      }

      // Build polygon from centerline
      const leftSide = [];
      const rightSide = [];

      for (let i = 0; i < centerLine.length; i++) {
        const curr = centerLine[i];
        const prev = centerLine[Math.max(0, i - 1)];
        const next = centerLine[Math.min(centerLine.length - 1, i + 1)];

        let dx, dy;
        if (i === 0) {
            dx = next[0] - curr[0];
            dy = next[1] - curr[1];
        } else if (i === centerLine.length - 1) {
            dx = curr[0] - prev[0];
            dy = curr[1] - prev[1];
        } else {
            dx = next[0] - prev[0];
            dy = next[1] - prev[1];
        }

        const len = Math.sqrt(dx * dx + dy * dy);
        if (len === 0) continue;

        const perpX = -dy / len;
        const perpY = dx / len;

        const metersPerDegreeLat = 111132;
        const metersPerDegreeLon = 111320 * Math.cos(curr[1] * Math.PI / 180);
        
        const offsetLon = (perpX * HALF_WIDTH) / metersPerDegreeLon;
        const offsetLat = (perpY * HALF_WIDTH) / metersPerDegreeLat;

        leftSide.push([curr[0] - offsetLon, curr[1] - offsetLat]);
        rightSide.push([curr[0] + offsetLon, curr[1] + offsetLat]);
      }

      const polygon = [...leftSide, ...rightSide.reverse()];
      if (polygon.length > 0) polygon.push(polygon[0]);

      const centerLng = centerLine.reduce((s, c) => s + c[0], 0) / centerLine.length;
      const centerLat = centerLine.reduce((s, c) => s + c[1], 0) / centerLine.length;

      return {
        ...zone,
        polygon,
        center: [centerLng, centerLat],
        centerLine: centerLine,
      };
    }).filter(Boolean);
  }, [grindZones, segments]);

  const selectedZone = useMemo(() => {
    if (!selectedZoneId) return null;
    return zonePolygons.find(z => z.id === selectedZoneId) || null;
  }, [selectedZoneId, zonePolygons]);

  const currentZoneIndex = selectedZone 
    ? zonePolygons.findIndex(z => z.id === selectedZone.id) 
    : -1;

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    mapRef.current = mapInstance;

    if (zonePolygons.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      zonePolygons.forEach(zone => {
        zone.polygon.forEach(coord => {
          bounds.extend({ lat: coord[1], lng: coord[0] });
        });
      });
      mapInstance.fitBounds(bounds);
      
      setTimeout(() => {
          mapInstance.setTilt(45);
      }, 500);
    }
  }, [zonePolygons]);

  useEffect(() => {
    if (map && selectedZone?.center) {
      map.panTo({ lat: selectedZone.center[1], lng: selectedZone.center[0] });
      if (map.getZoom() < 19) {
        map.setZoom(19);
      }
    }
  }, [selectedZone, map]);

  const adjustMap = useCallback((mode, amount) => {
    if (!mapRef.current) return;
    switch (mode) {
      case "tilt":
        mapRef.current.setTilt(Math.max(0, Math.min(67.5, mapRef.current.getTilt() + amount)));
        break;
      case "rotate":
        mapRef.current.setHeading((mapRef.current.getHeading() + amount + 360) % 360);
        break;
      default: break;
    }
  }, []);

  const navigateZone = useCallback((direction) => {
    if (!zonePolygons.length) return;
    let newIndex;
    if (currentZoneIndex === -1) {
      newIndex = 0;
    } else {
      newIndex = direction === 'next' 
        ? Math.min(currentZoneIndex + 1, zonePolygons.length - 1)
        : Math.max(currentZoneIndex - 1, 0);
    }
    onZoneSelect?.(zonePolygons[newIndex].id);
  }, [currentZoneIndex, zonePolygons, onZoneSelect]);

  // --------------------------------------------------------------------------
  // DECK.GL LAYERS
  // --------------------------------------------------------------------------

  const segmentLayers = useMemo(() => {
    if (!layerVisibility.segments) return [];

    const segmentData = segments.map(seg => {
      const geom = parseGeometry(seg.geom);
      if (!geom?.coordinates) return null;
      return {
        ...seg,
        path: geom.coordinates.map(c => [c[0], c[1], 0]),
      };
    }).filter(Boolean);

    return [
      new PathLayer({
        id: 'segment-casing',
        data: segmentData,
        getPath: d => d.path,
        getColor: [30, 30, 30, 200],
        getWidth: 12,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        parameters: { depthTest: false },
      }),
      new PathLayer({
        id: 'segment-line',
        data: segmentData,
        getPath: d => d.path,
        getColor: [100, 100, 100, 255],
        getWidth: 2,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        parameters: { depthTest: false },
      })
    ];
  }, [segments, layerVisibility.segments]);

  const zoneLayers = useMemo(() => {
    if (!layerVisibility.zones || zonePolygons.length === 0) return [];

    return [
      // Glow
      new PolygonLayer({
        id: 'grind-zone-glow',
        data: zonePolygons.filter(z => selectedZoneId === z.id),
        getPolygon: d => d.polygon,
        getFillColor: [255, 255, 255, 0],
        getLineColor: [255, 255, 255, 255],
        getLineWidth: 6,
        lineWidthUnits: 'pixels',
        filled: true,
        stroked: true,
        parameters: { depthTest: false },
      }),
      // Zone Body
      new PolygonLayer({
        id: 'grind-zone-fill',
        data: zonePolygons,
        getPolygon: d => d.polygon,
        getFillColor: d => getDepthColor(d.maxDepth),
        getLineColor: d => selectedZoneId === d.id ? [255, 255, 255, 255] : [180, 0, 0, 255],
        getLineWidth: d => selectedZoneId === d.id ? 3 : 2,
        lineWidthUnits: 'pixels',
        filled: true,
        stroked: true,
        pickable: true,
        autoHighlight: true,
        highlightColor: [255, 255, 255, 80],
        onClick: ({ object }) => object && onZoneSelect?.(object.id),
        onHover: ({ object, x, y }) => {
          if (object) {
            setHoverTooltip({
              x, y,
              content: { type: 'ZONE', ...object }
            });
          } else {
            setHoverTooltip(null);
          }
        },
      }),
      // Multi-Pass Lines
      new PathLayer({
        id: 'grind-zone-multipass',
        data: zonePolygons.filter(z => z.multiPass),
        getPath: d => d.centerLine.map(c => [c[0], c[1], 0.5]),
        getColor: [255, 255, 0, 200],
        getWidth: 2,
        widthUnits: 'pixels',
        getDashArray: [8, 4],
        dashJustified: true,
      }),
      // Labels (OFFSET REFINED)
      new TextLayer({
        id: 'grind-zone-labels',
        data: zonePolygons,
        getPosition: d => [...d.center, 0],
        getText: d => d.id,
        getColor: d => selectedZoneId === d.id ? [180, 0, 0, 255] : [255, 255, 255, 255],
        getSize: 14,
        fontFamily: 'Arial Black',
        background: true,
        getBackgroundColor: d => selectedZoneId === d.id ? [255,255,255,230] : [0,0,0,180],
        backgroundPadding: [4, 2],
        billboard: true,
        sizeScale: 1,
        sizeMinPixels: 12,
        sizeMaxPixels: 24,
        
        // --- OFFSET LOGIC ---
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'bottom',
        getPixelOffset: [0, -15], // Lifts text 15px above center
      })
    ];
  }, [zonePolygons, layerVisibility.zones, selectedZoneId, onZoneSelect]);

  const alrLayers = useMemo(() => {
    if (!layerVisibility.alrs || alrEvents.length === 0) return [];

    const alrData = alrEvents.map(ev => {
      const geom = parseGeometry(ev.geom);
      if (!geom?.coordinates) return null;
      return {
        ...ev,
        path: geom.coordinates.map(c => [c[0], c[1], 0.3]),
      };
    }).filter(Boolean);

    return [
      new PathLayer({
        id: 'alr-casing',
        data: alrData,
        getPath: d => d.path,
        getColor: [0, 0, 0, 255],
        getWidth: 6,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
      }),
      new PathLayer({
        id: 'alr-fill',
        data: alrData,
        getPath: d => d.path,
        getColor: d => getALRColor(d.magnitude_in || 0),
        getWidth: 3,
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        pickable: true,
        onHover: ({ object, x, y }) => {
          if (object) {
            setHoverTooltip({
              x, y,
              content: { type: 'ALR', ...object }
            });
          } else {
            setHoverTooltip(null);
          }
        },
      }),
    ];
  }, [alrEvents, layerVisibility.alrs]);

  useEffect(() => {
    if (!map) return;

    if (!deckOverlayRef.current) {
      const overlay = new GoogleMapsOverlay({ layers: [] });
      overlay.setMap(map);
      deckOverlayRef.current = overlay;
    }

    const allLayers = [
      ...segmentLayers,
      ...zoneLayers,
      ...alrLayers,
    ];

    deckOverlayRef.current.setProps({ layers: allLayers });
  }, [map, segmentLayers, zoneLayers, alrLayers]);

  useEffect(() => {
    return () => {
      if (deckOverlayRef.current) {
        deckOverlayRef.current.setMap(null);
        deckOverlayRef.current.finalize();
      }
    };
  }, []);

  if (loadError) return <div className="h-96 flex items-center justify-center bg-gray-100 text-red-500">Map Error</div>;
  if (!isLoaded) return <div className="h-96 flex items-center justify-center bg-gray-100"><ArrowPathIcon className="h-5 w-5 animate-spin mr-2"/>Loading Map...</div>;

  return (
    <div className="flex flex-col h-[600px] border border-gray-200 rounded-sm overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
        <div className="inline-flex rounded overflow-hidden border border-gray-200">
          {[
            { key: 'zones', label: 'Zones' },
            { key: 'alrs', label: 'ALRs' },
            { key: 'segments', label: 'Segs' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setLayerVisibility(prev => ({ ...prev, [key]: !prev[key] }))}
              className={`px-2 py-1 text-xs font-medium transition-colors ${
                layerVisibility[key]
                  ? 'bg-red-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigateZone('prev')}
            disabled={currentZoneIndex <= 0}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium min-w-[100px] text-center">
            {selectedZone ? (
              <>{selectedZone.id} <span className="text-gray-400 text-xs">({currentZoneIndex + 1}/{zonePolygons.length})</span></>
            ) : (
              <span className="text-gray-400">No selection</span>
            )}
          </span>
          <button
            onClick={() => navigateZone('next')}
            disabled={currentZoneIndex >= zonePolygons.length - 1}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="inline-flex rounded overflow-hidden border border-gray-200">
          <button onClick={() => adjustMap("rotate", 45)} className="p-1.5 bg-white hover:bg-gray-50 text-gray-600"><RotateCcwSquare className="h-4 w-4" /></button>
          <button onClick={() => adjustMap("tilt", -15)} className="p-1.5 bg-white hover:bg-gray-50 text-gray-600 border-l border-gray-200"><ArrowUpFromLine className="h-4 w-4" /></button>
          <button onClick={() => adjustMap("tilt", 15)} className="p-1.5 bg-white hover:bg-gray-50 text-gray-600 border-l border-gray-200"><ArrowDownFromLine className="h-4 w-4" /></button>
          <button onClick={() => adjustMap("rotate", -45)} className="p-1.5 bg-white hover:bg-gray-50 text-gray-600 border-l border-gray-200"><RotateCwSquare className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="flex-grow relative">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          options={mapOptions}
          onLoad={onMapLoad}
        />

        {hoverTooltip && (
          <div
            className="fixed bg-gray-900 text-white px-3 py-2 rounded shadow-lg text-xs z-[2000] pointer-events-none"
            style={{
              left: hoverTooltip.x + 12,
              top: hoverTooltip.y - 12,
              transform: 'translateY(-100%)'
            }}
          >
            {hoverTooltip.content.type === 'ALR' ? (
              <>
                <div className="font-semibold text-amber-400 mb-1">ALR Event</div>
                <div>{Math.round(hoverTooltip.content.magnitude_in)} in/mi</div>
                <div className="text-gray-400">{formatStation(hoverTooltip.content.from_station_ft)}</div>
              </>
            ) : (
              <>
                <div className="font-semibold text-red-400 mb-1">{hoverTooltip.content.id}</div>
                <div>Len: {hoverTooltip.content.length?.toFixed(0)} ft</div>
                <div>Depth: {hoverTooltip.content.maxDepth?.toFixed(3)}"</div>
                {hoverTooltip.content.multiPass && <div className="text-orange-300 mt-1">Multi-Pass</div>}
              </>
            )}
          </div>
        )}
      </div>

      {selectedZone && (
        <div className="border-t border-gray-200 bg-white">
          <button
            onClick={() => setDetailsExpanded(!detailsExpanded)}
            className="w-full px-3 py-1.5 flex items-center justify-between hover:bg-gray-50 border-b border-gray-100"
          >
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-gray-900">{selectedZone.id}</span>
              {selectedZone.multiPass && (
                <span className="px-1.5 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">
                  {selectedZone.passCount} Passes
                </span>
              )}
              {selectedZone.strategy === 'continuous' && (
                <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                  Production
                </span>
              )}
              {selectedZone.dipRelated && (
                <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" title="Dip-related" />
              )}
            </div>
            {detailsExpanded ? (
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronUpIcon className="h-4 w-4 text-gray-400" />
            )}
          </button>

          {detailsExpanded && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-1.5 text-left font-medium text-gray-600">From</th>
                    <th className="px-3 py-1.5 text-left font-medium text-gray-600">To</th>
                    <th className="px-3 py-1.5 text-left font-medium text-gray-600">Length</th>
                    <th className="px-3 py-1.5 text-left font-medium text-gray-600">Max Depth</th>
                    <th className="px-3 py-1.5 text-left font-medium text-gray-600">Passes</th>
                    <th className="px-3 py-1.5 text-left font-medium text-gray-600">Triggers</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-3 py-2 font-mono">{formatStation(selectedZone.systemStartStation)}</td>
                    <td className="px-3 py-2 font-mono">{formatStation(selectedZone.systemEndStation)}</td>
                    <td className="px-3 py-2">{selectedZone.length?.toFixed(0)} ft</td>
                    <td className="px-3 py-2">{selectedZone.maxDepth?.toFixed(3)}"</td>
                    <td className="px-3 py-2">
                      {selectedZone.passDepths?.map((d, i) => (
                        <span key={i} className={`inline-block mr-1 px-1 py-0.5 rounded ${selectedZone.multiPass ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-600'}`}>
                          P{i + 1}: {d.toFixed(3)}"
                        </span>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-gray-500 max-w-xs truncate" title={selectedZone.triggers?.join('; ')}>
                      {selectedZone.triggers?.slice(0, 2).join('; ') || '—'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="px-3 py-1.5 border-t border-gray-200 bg-gray-50 flex items-center gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 rounded-sm" style={{ background: 'rgba(255, 150, 150, 0.8)' }} />
          <span className="text-gray-500">&lt;0.25"</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 rounded-sm" style={{ background: 'rgba(255, 80, 80, 0.8)' }} />
          <span className="text-gray-500">0.25-0.5"</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 rounded-sm" style={{ background: 'rgba(220, 40, 40, 0.8)' }} />
          <span className="text-gray-500">&gt;0.5"</span>
        </div>
        <div className="w-px h-3 bg-gray-300" />
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0.5 rounded" style={{ background: 'rgb(255, 193, 7)' }} />
          <span className="text-gray-500">ALR</span>
        </div>
      </div>
    </div>
  );
}

export default GrindZoneMapTab;